import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../constants/dataAids';
import { SquarePaymentForm, SquareGiftCardForm, PaypalButton } from '@wsb/securepay-payment';
import { SeparatorTextLine } from '@wsb/ola-widget-components';
import FormattersStore from '../stores/Formatters';
import { SQUARE, PAYPAL } from '../constants/paymentMethods';
import {
  startPayment,
  cancelPayment,
  initPaypal,
  savePaypalBooking,
  saveSquareBooking,
  receivePaypalError
} from '../actions/BookingForm';
import { Translate as translate } from '@wsb/vnext-widget-content-translate';
import { trackMetric } from '../utils/trackMetric';
import TrackImpression from './TrackImpression';
import {
  BOOKING_FORM_PAYPAL_PAYMENT_METHOD_IMPRESSION,
  BOOKING_FORM_PAYPAL_PAYMENT_METHOD_CLICK,
  BOOKING_FORM_SQUARE_PAYMENT_METHOD_IMPRESSION,
  BOOKING_FORM_SQUARE_PAYMENT_METHOD_SUBMIT_CTA_CLICK
} from '../constants/metrics';

const squareAlertClass = 'sp-alert';

const styles = {
  paymentSection: {
    width: '100%'
  },
  paymentBlock: {
    marginTop: 'medium'
  },
  paymentTitle: {
    marginBottom: 'medium',
    font: 'alt',
    fontSize: 'large',
    fontColor: 'highContrast'
  },
  deposit: {
    marginBottom: 'small'
  },
  depositAmount: {
    fontWeight: 'bold'
  },
  paymentRowStyles: {
    col: {
      marginBottom: 'medium',
      paddingHorizontal: 'xsmall',
      '@sm': {
        paddingHorizontal: 'small'
      }
    },
    col100: {
      flexBasis: '100%',
      maxWidth: '100%'
    },
    col50: {
      flexBasis: '50%',
      maxWidth: '50%'
    },
    col33Sm: {
      '@sm': {
        paddingHorizontal: 'small', // duplicates rowStyles.col, but UX2 doesn't consolidate multiple media queries
        flexBasis: '33.33333333333333%',
        maxWidth: '33.33333333333333%'
      }
    }
  },
  paymentButtonStyles: {
    paddingHorizontal: 'large',
    paddingVertical: 'small',
    borderStyle: 'none',
    borderRadius: '3px',
    backgroundColor: 'action',
    font: 'alt',
    fontWeight: 'bold',
    fontColor: 'action',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    outline: 'none',
    '@xs-only': {
      width: '100%'
    }
  },
  ccIconStyles: {
    display: 'inline-block',
    height: '26px',
    width: '42px',
    marginRight: 'xxsmall',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '2px',
    backgroundColor: '!#ffffff',
    '@sm': {
      height: '29px',
      width: '47px'
    }
  },
  separatorLine: {
    marginTop: 'large'
  },
  squareInput: {
    fontSize: '16px',
    padding: '15px',
    backgroundColor: '#ffffff'
  }
};

const squareCCIconList = [
  'visa',
  'masterCard',
  'americanExpress',
  'discover',
  'discoverDiners',
  'JCB'
];

const squarePaymentText = {
  expiration: 'MM / YY',
  postalCode: 'Postal / Zip'
};

export default class PaymentSection extends Component {
  static contextTypes = UX2.Base.contextTypes;
  constructor(props) {
    super(props);
    this.onSquareError = this.onSquareError.bind(this);
  }

  convertToClassNames(style) {
    return this.getRenderedClassNames(UX2.Element.Element, { style });
  }

  getRenderedClassNames(ElementComponent, props = null) {
    const el = new ElementComponent(props, this.context);
    const instance = el.render ? el.render() : el;
    return typeof instance.type === 'function'
      ? this.getRenderedClassNames(instance.type, instance.props)
      : instance.props.className || '';
  }

  onPaypalClick = () => {
    trackMetric(BOOKING_FORM_PAYPAL_PAYMENT_METHOD_CLICK);
    return initPaypal();
  };

  getSquareVerificationDetails() {
    const { paymentInfo, client } = this.props;
    const { numberFormatter } = FormattersStore.getState();

    return {
      amount: numberFormatter.formatCurrency(+paymentInfo.amount, { withSymbol: false }),
      currencyCode: paymentInfo.currency,
      billingContact: {
        familyName: client.last_name,
        givenName: client.first_name,
        email: client.email,
        phone: client.phone_number
      }
    };
  }

  onAuthorized(data, paymentMethod) {
    const { onBookingSave } = this.props;
    if (paymentMethod === SQUARE) {
      saveSquareBooking(data).then(onBookingSave);
    }
    if (paymentMethod === PAYPAL) {
      savePaypalBooking(data).then(onBookingSave);
    }
  }

  onSquareError(e) {
    const { staticContent } = this.props;
    // Hack to replace Square error message with our own
    if (e[0].type === 'VALIDATION_ERROR') {
      const errorEl = document.getElementsByClassName(squareAlertClass)[0];
      if (errorEl) {
        errorEl.innerHTML = staticContent.giftCardRedemptionCodeInvalid;
      }
      ``;
    }
    cancelPayment();
  }

  onSquareSubmitClick = () => {
    trackMetric(BOOKING_FORM_SQUARE_PAYMENT_METHOD_SUBMIT_CTA_CLICK);
    startPayment();
  };

  render() {
    const {
      titleStyles,
      paymentMethods,
      isPublishMode,
      service,
      isPartialPayment,
      isPaypalOn,
      isSquareOn,
      isSquareGiftCardApplicable,
      env,
      paypalErrors,
      staticContent,
      submitText,
      isFormValid
    } = this.props;

    const { Block, Heading, Text } = UX2.Element;
    const { numberFormatter } = FormattersStore.getState();

    const paymentStyles = {
      loading: this.convertToClassNames({
        width: '100%',
        marginVertical: 'large',
        font: 'alt'
      }),
      inputSection: this.convertToClassNames({
        marginTop: 'small'
      }),
      form: this.convertToClassNames({
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        marginLeft: '-xsmall',
        marginRight: '-xsmall',
        '@sm': {
          marginLeft: '-small',
          marginRight: '-small'
        }
      }),
      inputRowCard: this.convertToClassNames({
        ...styles.paymentRowStyles.col,
        ...styles.paymentRowStyles.col100
      }),
      inputRowExpiration: this.convertToClassNames({
        ...styles.paymentRowStyles.col,
        ...styles.paymentRowStyles.col50,
        ...styles.paymentRowStyles.col33Sm
      }),
      inputRowCVV: this.convertToClassNames({
        ...styles.paymentRowStyles.col,
        ...styles.paymentRowStyles.col50,
        ...styles.paymentRowStyles.col33Sm
      }),
      inputRowPostalCode: this.convertToClassNames({
        ...styles.paymentRowStyles.col,
        ...styles.paymentRowStyles.col100,
        ...styles.paymentRowStyles.col33Sm
      }),
      cardWrapper: this.convertToClassNames({
        position: 'relative',
        fontColor: '!#999999'
      }),
      lockIcon: this.convertToClassNames({
        position: 'absolute',
        top: '15px',
        right: '16px',
        width: '12px',
        height: '18px',
        fill: 'currentColor',
        zIndex: '2'
      }),
      ccIconWrapper: this.convertToClassNames({
        marginBottom: 'medium'
      }),
      ccIcon: this.convertToClassNames({
        ...styles.ccIconStyles,
        borderColor: '!#bbbbbb'
      }),
      ccIconActive: this.convertToClassNames({
        ...styles.ccIconStyles,
        borderColor: '!#777777'
      }),
      ccDefaultDark: this.convertToClassNames({
        fill: '#999999 !important'
      }),
      ccDefaultLight: this.convertToClassNames({
        fill: '#bbbbbb !important'
      }),
      iframeWrapper: this.convertToClassNames({
        width: '1px', // iOS workaround for iframe widths
        minWidth: '100%',
        position: 'relative',
        height: '48px',
        borderWidth: '1px',
        borderColor: 'input',
        borderStyle: 'solid',
        backgroundColor: '#ffffff',
        zIndex: '1'
      }),
      label: this.convertToClassNames({
        display: 'block',
        marginBottom: 'xxsmall',
        font: 'alt',
        fontColor: 'lowContrast'
      }),
      paymentRow: this.convertToClassNames({
        marginTop: 'small',
        font: 'alt',
        fontColor: 'section'
      }),
      button: this.convertToClassNames({
        ...styles.paymentButtonStyles
      }),
      buttonDisabled: this.convertToClassNames({
        ...styles.paymentButtonStyles,
        opacity: '0.5',
        cursor: 'not-allowed'
      }),
      error: `${squareAlertClass} ${this.convertToClassNames({
        width: '100%',
        marginTop: 'small',
        font: 'alt',
        fontColor: 'error'
      })}`,
      note: this.convertToClassNames({
        marginBottom: 'small',
        color: '#5e5e5e'
      })
    };
    const paypalStyles = {
      wrapper: this.convertToClassNames({
        marginTop: 'small'
      }),
      buttonWrapper: this.convertToClassNames({
        '@sm': {
          maxWidth: '50%'
        },
        '@md': {
          maxWidth: '33%'
        }
      }),
      errors: {
        errorWrapper: this.convertToClassNames({
          marginTop: 'small'
        }),
        error: this.convertToClassNames({
          marginTop: 'xsmall',
          font: 'alt',
          fontColor: 'error'
        })
      }
    };

    const interpolate = translate(staticContent);
    const depositAmount = Number(service.deposit_amount);

    return (
      <Block style={ styles.paymentSection }>
        { isPartialPayment && (
          <Text style={ styles.deposit }>
            { interpolate('paymentInfo', {
              amount: () => (
                <Text
                  data-aid={ dataAids.BOOKING_FORM_DEPOSIT_AMOUNT }
                  tag='span'
                  style={ styles.depositAmount }
                >
                  { numberFormatter.formatCurrency(+depositAmount) }
                </Text>
              )
            }) }
          </Text>
        ) }
        { isPaypalOn && (
          <Block style={ styles.paymentBlock }>
            <Heading.Minor style={ titleStyles }>{ staticContent.paymentPaypalCheckout }</Heading.Minor>
            <TrackImpression metric={ BOOKING_FORM_PAYPAL_PAYMENT_METHOD_IMPRESSION }>
              <PaypalButton
                data-aid={ dataAids.BOOKING_FORM_PAYPAL_BUTTON }
                env={ env }
                isDisabled={ !isPublishMode }
                onPaymentInitialize={ this.onPaypalClick }
                onAuthorize={ data => this.onAuthorized(data, PAYPAL) }
                classNames={ paypalStyles }
                onCancel={ cancelPayment }
                onError={ err => receivePaypalError({ type: 'paypal_error', error: err }) }
                errors={ paypalErrors }
              />
            </TrackImpression>
          </Block>
        ) }
        { isPaypalOn && isSquareOn && (
          <SeparatorTextLine text={ staticContent.or } separatorStyles={ styles.separatorLine } />
        ) }
        { isSquareOn && (
          <Block style={ styles.paymentBlock }>
            <Heading.Minor style={ titleStyles }>
              { staticContent.paymentCreditCardCheckout }
            </Heading.Minor>
            <TrackImpression metric={ BOOKING_FORM_SQUARE_PAYMENT_METHOD_IMPRESSION }>
              <SquarePaymentForm
                isFormDisabled={ !isPublishMode }
                onSubmit={ this.onSquareSubmitClick }
                onAuthorized={ data => this.onAuthorized(data, SQUARE) }
                verificationDetails={ this.getSquareVerificationDetails() }
                onError={ cancelPayment }
                inputStyles={ [styles.squareInput] }
                ccIconList={ squareCCIconList }
                classNames={ paymentStyles }
                paymentText={{
                  button: submitText,
                  ...squarePaymentText
                }}
                isDisabled={ !isFormValid || !isPublishMode }
                paymentMethods={ paymentMethods }
                data-aid={ dataAids.BOOKING_FORM_SQUARE_FORM }
              />
            </TrackImpression>
          </Block>
        ) }
        { isSquareGiftCardApplicable && (
          <Fragment>
            <SeparatorTextLine text='or' separatorStyles={ styles.separatorLine } />
            <Block style={ styles.paymentBlock }>
              <Heading.Minor style={ titleStyles }>
                { staticContent.paymentGiftCardCheckout }
              </Heading.Minor>
              <SquareGiftCardForm
                classNames={ paymentStyles }
                inputStyles={ [styles.squareInput] }
                paymentText={{
                  ...squarePaymentText,
                  button: submitText,
                  cardNumber: staticContent.giftCardRedemptionCode,
                  cardNote: staticContent.giftCardDisclaimer
                }}
                isFormDisabled={ !isPublishMode }
                isDisabled={ !isFormValid || !isPublishMode }
                paymentMethods={ paymentMethods }
                onSubmit={ this.onSquareSubmitClick }
                onAuthorized={ data => this.onAuthorized(data, SQUARE) }
                onError={ this.onSquareError }
                data-aid={ dataAids.BOOKING_FORM_SQUARE_GIFT_CARD_FORM }
              />
            </Block>
          </Fragment>
        ) }
      </Block>
    );
  }
}

PaymentSection.propTypes = {
  titleStyles: PropTypes.object,
  paymentMethods: PropTypes.array,
  isPublishMode: PropTypes.bool,
  service: PropTypes.object,
  client: PropTypes.object,
  paymentInfo: PropTypes.object,
  isPartialPayment: PropTypes.bool,
  isPaypalOn: PropTypes.bool,
  isSquareOn: PropTypes.bool,
  isSquareGiftCardApplicable: PropTypes.bool,
  env: PropTypes.string,
  paypalErrors: PropTypes.array,
  staticContent: PropTypes.object.isRequired,
  submitText: PropTypes.string,
  isFormValid: PropTypes.bool,
  onBookingSave: PropTypes.func
};
